import { type ElementType } from 'react';
import { cn } from '@/utils/cn';
import { Icon } from '../Icon';
import { Typography } from '../Typography';

export type ProductSelectCardState = 'default' | 'selected' | 'outOfStock';

/** Sealed — no `className`; layout belongs on the parent. */
export interface ProductSelectCardProps {
  productName: string;
  brandName?: string;
  /** A placeholder is shown when omitted. */
  imageUrl?: string | null;
  /** Shown in the `default` state (e.g. "10 days left"). */
  badgeLabel?: string;
  /** `selected` → success border + check; `outOfStock` → dimmed, disabled, "Out of stock". */
  state?: ProductSelectCardState;
  /** When provided, the card renders as an interactive `<button>`. */
  onClick?: () => void;
}

/** Selectable product card, mirroring the mobile `ProductCardPrice` 3-state pattern. */
export function ProductSelectCard({
  productName,
  brandName,
  imageUrl,
  badgeLabel,
  state = 'default',
  onClick,
}: ProductSelectCardProps) {
  const isSelected = state === 'selected';
  const isOutOfStock = state === 'outOfStock';
  const Root: ElementType = onClick ? 'button' : 'div';

  return (
    <Root
      {...(onClick
        ? { type: 'button' as const, onClick, disabled: isOutOfStock, 'aria-pressed': isSelected }
        : {})}
      className={cn(
        'w-full text-left rounded-medium border bg-surface overflow-hidden',
        'shadow-(--shadow-top-light-first-floor) transition-colors',
        isSelected ? 'border-success' : 'border-default',
        isOutOfStock && 'opacity-60 cursor-not-allowed'
      )}
    >
      <div className="flex flex-row items-stretch p-4 gap-3">
        <div className="w-2/5 aspect-square flex-shrink-0">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={productName}
              className="w-full h-full object-cover object-center"
            />
          ) : (
            <div className="w-full h-full bg-disabled" aria-hidden="true" />
          )}
        </div>

        <div className="border-l border-default self-stretch" aria-hidden="true" />

        <div className="flex flex-col gap-1 justify-center flex-1 min-w-0">
          {brandName && (
            <Typography variant="body" size="small" color="bodylight">
              {brandName}
            </Typography>
          )}
          <Typography variant="header" size="h3" as="h3">
            {productName}
          </Typography>
          {isOutOfStock ? (
            <Typography variant="body" size="small" color="error">
              Out of stock
            </Typography>
          ) : (
            !isSelected &&
            badgeLabel && (
              <span className="self-start inline-flex items-center rounded-rounded bg-success px-2 py-1">
                <Typography variant="body" size="xs" color="success">
                  {badgeLabel}
                </Typography>
              </span>
            )
          )}
        </div>

        {isSelected && (
          <div className="self-center">
            <Icon name="circle-tick-filled" size="large" color="success" />
          </div>
        )}
      </div>
    </Root>
  );
}

export default ProductSelectCard;
